/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import { Shuffle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { badgePalette, randomBadgeColor } from './badge-utils';

/**
 * `Props` type.
 */

type Props = {
  onChange: (color: string) => void;
  value: string;
};

/**
 * Export `BadgeColorPicker` — a row of preset swatches plus a "generate random
 * color" button (GitHub-label style).
 */

export function BadgeColorPicker({ onChange, value }: Props): JSX.Element {
  const [translate] = useTranslation();

  return (
    <div className={'flex flex-wrap items-center gap-1.5'}>
      <button
        aria-label={translate('bankAccounts.movements.badges.generate')}
        className={
          'grid h-6 w-6 place-items-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        }
        onClick={() => onChange(randomBadgeColor(value))}
        title={translate('bankAccounts.movements.badges.generate')}
        type={'button'}
      >
        <Shuffle className={'h-3.5 w-3.5'} />
      </button>

      {badgePalette.map(color => (
        <button
          aria-label={color}
          className={cn(
            'h-6 w-6 rounded-full border transition-transform hover:scale-110',
            value.toLowerCase() === color.toLowerCase()
              ? 'border-foreground ring-2 ring-foreground/20'
              : 'border-black/10'
          )}
          key={color}
          onClick={() => onChange(color)}
          style={{ backgroundColor: color }}
          title={color}
          type={'button'}
        />
      ))}
    </div>
  );
}
